/**
 * oRPC Client — 全 Contract を統合した単一クライアント
 *
 * 新しい API を追加する場合:
 * 1. Contract を定義（共有パッケージ or ローカル）
 * 2. appContract に追加
 * 3. client.xxx.method() で呼び出し
 */
import { createORPCClient } from '@orpc/client';
import { oc, type ContractRouterClient } from '@orpc/contract';
import { OpenAPILink } from '@orpc/openapi-client/fetch';
import { appIntegrationsForSelectContract, chatContract, contractAlertContract, customerLedgerContract, inboundDataPipelineContract, inboundDataPipelineEnumsContract, dealLedgerContract, findPersonHistoriesFromEventStoreContract, storageSourceTypesForSelectContract, individualEquipmentContract, individualCertificationLedgerContract, individualAccountLedgerContract, individualDeviceLedgerContract, individualEquipmentLedgerContract, individualPlaceLedgerContract, dataScopePermissionContract, listDeviceCostByMemberContract, listDeviceCostByMemberForDeviceCatalogContract, listDevicePaymentByMemberContract, listDevicePaymentByMemberForDeviceCatalogContract, listSoftwareCostByMemberContract, listSoftwareCostByMemberForSoftwareContract, listSoftwarePaymentByMemberContract, listSoftwarePaymentByMemberForSoftwareContract, listPeopleOperationActivityContract, listPeopleOperationActivityTypesContract, oauth2Contract, openidMetadataContract, outboundDataPipelineConfigContract, outboundDataPipelineTaskContract, peopleCsvContract, peopleCsvImportJobContract, s3Contract, softwareContract, ssoIntegrationsContract, summaryPeopleOperationActivityContract, supplierLedgerContract, supplierLedgerSettingsContract, workModeContract, loginEmailPolicyContract, amendEgovApplicationByStepInstanceContract, bulkSubmitEgovApplicationsByStepInstancesContract, bulkSyncEgovApplicationsByStepInstancesContract, submitEgovApplicationByStepInstanceContract, syncEgovApplicationStatusContract, withdrawEgovApplicationByStepInstanceContract, documentSelectionPreviewContract, equipmentAlertContract, equipmentAssignmentRuleContract, equipmentListContract, equipmentPeriodRuleContract, equipmentSettingContract, listDeviceCostByDepartmentContract, listDeviceCostByDepartmentForDeviceCatalogContract, listDevicePaymentByDepartmentContract, listDevicePaymentByDepartmentForDeviceCatalogContract, listSoftwareCostByDepartmentContract, listSoftwareCostByDepartmentForSoftwareContract, listSoftwarePaymentByDepartmentContract, listSoftwarePaymentByDepartmentForSoftwareContract, countYearEndAdjustmentInstancesByTagDocumentTabsContract, getYearEndAdjustmentInstanceDocumentsContract, listYearEndAdjustmentInstancesByTagContract, publicIdContract, publicIdDetailContract, publicIdFilesContract, publicIdRevealContract, businessPartnerOverviewContract, inboundSsoContract, outboundSsoSoftwaresContract } from '@standandforce/api-contract';
import { authFetch, getBaseUrl } from './auth-fetch';
// TODO(F-1): `@standandforce/api-contract@0.0.1062` が install 可能になったら
// ローカル Contract を廃し、`accessesAiContract` を `@standandforce/api-contract` から import する。
import { accessesAiContract } from './contracts/accesses-ai';
import { getAppIntegrationGwsConnectionRoute, patchAppIntegrationGwsConnectionRoute } from './contracts/app-integration-gws-connection';
// TODO: `@standandforce/api-contract` に content 対応版が publish されたら差し替える
import { authPasswordContract } from './contracts/auth-password';
import { boePackageNotificationConfigContract } from './contracts/boe-package-notification-config';
import { contractLedgerContract } from './contracts/contract-ledger';
import { contractOverviewContract } from './contracts/contract-overview';
import { inboundDataPipelineCsvContract } from './contracts/data-pipeline-csv';
// TODO(api-contract): deviceAssignmentRule を @standandforce/api-contract へ差し替え（publish 後）
import { deviceAssignmentInclusionContract } from './contracts/device-assignment-inclusion';
import { deviceAssignmentRuleContract } from './contracts/device-assignment-rule';
import { deviceGroupContract } from './contracts/device-group';
import { equipmentOverviewContract } from './contracts/equipment-overview';
import { fleetOverviewContract } from './contracts/fleet-overview';
import { govProfileSubmittableContract } from './contracts/gov-profile-submittable';
import { itemLedgerContract } from './contracts/item-ledger';
// TODO(api-contract): correctionSubmissionMode 入りが publish されたら
// `@standandforce/api-contract` へ差し替え、ローカル Contract を削除する。
import { listEgovApplicationStepsByPackageInstancesContract, listEgovApplicationStepsByTagContract } from './contracts/labor-core-egov-application-steps';
import { startLaborCoreOperationPackagePatternContract } from './contracts/labor-core-start-operation-package-pattern';
import { loginMethodPolicyContract } from './contracts/login-method-policy';
import { notificationAndResponsePackageAssignmentsContract, notificationAndResponsePoliciesContract, notificationAndResponseRecipientOptionsContract, notificationAndResponseVariablesContract } from './contracts/notification-and-response-policies';
import { passwordPolicyContract } from './contracts/password-policy';
import { placeOverviewContract } from './contracts/place-overview';
// TODO(alumni-overview): BE の `overviewTenantAlumniAppUser` が
// `@standandforce/api-contract` に publish されたら、このローカル Contract を廃し
// `orpc.software.overviewTenantAlumniAppUser`（softwareContract 由来）へ差し替える。
import { softwareOverviewAlumniContract } from './contracts/software-overview-alumni';
// TODO(training): backend の training contract が publish されたら
// `@standandforce/api-contract` の import に切り替え、ローカル Contract を削除する
import { getSoftwareIntegrationSettingsWithTrainingRoute, softwareTrainingContract } from './contracts/software-training';
// public-id の contract は BE の @standandforce/api-contract を真実源とする（上の package import に統合済み）。
// マージ前の差し替え強制: publish 前は手元 file:../ ローカルリンクで解決して dev は動くが、package.json を
// bump/commit しないため CI は pin 済み旧バージョンで publicId*Contract を解決できず落ちる。
// ローカルミラー（contracts/public-id{,-detail,-files,-reveal}.ts）は型 import 用に残置。

/**
 * 全 Contract を 1 つの Router にマージ。
 * ファイルアップロード用ローカル Contract と共有パッケージ Contract を統合。
 */
export const appContract = oc.router({
  workMode: workModeContract,
  s3: s3Contract,
  peopleCsv: peopleCsvContract,
  peopleCsvImportJob: peopleCsvImportJobContract,
  inboundDataPipelineCsv: inboundDataPipelineCsvContract,
  inboundDataPipeline: inboundDataPipelineContract,
  inboundDataPipelineEnums: inboundDataPipelineEnumsContract,
  outboundDataPipeline: outboundDataPipelineConfigContract,
  outboundDataPipelineTask: outboundDataPipelineTaskContract,
  appIntegrationsForSelect: appIntegrationsForSelectContract,
  storageSourceTypesForSelect: storageSourceTypesForSelectContract,
  contractAlert: contractAlertContract,
  contractLedger: contractLedgerContract,
  contractOverview: contractOverviewContract,
  loginEmailPolicy: loginEmailPolicyContract,
  loginMethodPolicy: loginMethodPolicyContract,
  passwordPolicy: passwordPolicyContract,
  authPassword: authPasswordContract,
  software: {
    ...softwareContract,
    // training セクション付き設定 GET（publish 前のローカル差し替え。path は package 版と同一）
    integrationSettings: getSoftwareIntegrationSettingsWithTrainingRoute,
    training: softwareTrainingContract
  },
  softwareOverviewAlumni: softwareOverviewAlumniContract,
  accessesAi: accessesAiContract,
  appIntegration: oc.router({
    gwsConnection: patchAppIntegrationGwsConnectionRoute,
    getGwsConnection: getAppIntegrationGwsConnectionRoute
  }),
  individualEquipment: individualEquipmentContract,
  equipmentList: equipmentListContract,
  individualLedger: oc.router({
    devices: individualDeviceLedgerContract,
    equipments: individualEquipmentLedgerContract,
    places: individualPlaceLedgerContract,
    certifications: individualCertificationLedgerContract,
    accounts: individualAccountLedgerContract
  }),
  dataScopePermission: dataScopePermissionContract,
  equipmentAlert: equipmentAlertContract,
  equipmentAssignmentRule: equipmentAssignmentRuleContract,
  equipmentPeriodRule: equipmentPeriodRuleContract,
  equipmentSetting: equipmentSettingContract,
  deviceCost: oc.router({
    byMember: listDeviceCostByMemberContract,
    byDepartment: listDeviceCostByDepartmentContract,
    byMemberForDeviceCatalog: listDeviceCostByMemberForDeviceCatalogContract,
    byDepartmentForDeviceCatalog: listDeviceCostByDepartmentForDeviceCatalogContract
  }),
  devicePayment: oc.router({
    byMember: listDevicePaymentByMemberContract,
    byDepartment: listDevicePaymentByDepartmentContract,
    byMemberForDeviceCatalog: listDevicePaymentByMemberForDeviceCatalogContract,
    byDepartmentForDeviceCatalog: listDevicePaymentByDepartmentForDeviceCatalogContract
  }),
  softwareCost: oc.router({
    byMember: listSoftwareCostByMemberContract,
    byDepartment: listSoftwareCostByDepartmentContract,
    byMemberForSoftware: listSoftwareCostByMemberForSoftwareContract,
    byDepartmentForSoftware: listSoftwareCostByDepartmentForSoftwareContract
  }),
  softwarePayment: oc.router({
    byMember: listSoftwarePaymentByMemberContract,
    byDepartment: listSoftwarePaymentByDepartmentContract,
    byMemberForSoftware: listSoftwarePaymentByMemberForSoftwareContract,
    byDepartmentForSoftware: listSoftwarePaymentByDepartmentForSoftwareContract
  }),
  listPeopleOperationActivity: listPeopleOperationActivityContract,
  listPeopleOperationActivityTypes: listPeopleOperationActivityTypesContract,
  placeOverview: placeOverviewContract,
  fleetOverview: fleetOverviewContract,
  equipmentOverview: equipmentOverviewContract,
  businessPartnerOverview: businessPartnerOverviewContract,
  summaryPeopleOperationActivity: summaryPeopleOperationActivityContract,
  dealLedger: dealLedgerContract,
  customerLedger: {
    customersList: customerLedgerContract.customersList,
    customersCounts: customerLedgerContract.customersCounts,
    customers: {
      create: customerLedgerContract.customerCreate,
      getProfileHeader: customerLedgerContract.customerProfileHeader,
      getDetail: customerLedgerContract.customerDetail,
      update: customerLedgerContract.customerUpdate,
      commercialTerms: {
        create: customerLedgerContract.commercialTermsCreate,
        update: customerLedgerContract.commercialTermsUpdate,
        delete: customerLedgerContract.commercialTermsDelete
      }
    }
  },
  itemLedger: {
    itemsList: itemLedgerContract.itemsList,
    itemsDetail: itemLedgerContract.itemsDetail,
    itemsCreate: itemLedgerContract.itemsCreate,
    itemsUpdate: itemLedgerContract.itemsUpdate,
    itemsChangeStatus: itemLedgerContract.itemsChangeStatus,
    procurementProfileCreate: itemLedgerContract.procurementProfileCreate,
    procurementProfileUpdate: itemLedgerContract.procurementProfileUpdate,
    procurementProfileChangeStatus: itemLedgerContract.procurementProfileChangeStatus,
    salesProfileCreate: itemLedgerContract.salesProfileCreate,
    salesProfileUpdate: itemLedgerContract.salesProfileUpdate,
    salesProfileChangeStatus: itemLedgerContract.salesProfileChangeStatus
  },
  supplierLedger: {
    suppliersList: supplierLedgerContract.suppliersList,
    suppliersCounts: supplierLedgerContract.suppliersCounts,
    suppliers: {
      create: supplierLedgerContract.supplierCreate,
      getProfileHeader: supplierLedgerContract.supplierProfileHeader,
      getDetail: supplierLedgerContract.supplierDetail,
      update: supplierLedgerContract.supplierUpdate,
      avlEntries: {
        create: supplierLedgerContract.avlEntryCreate,
        update: supplierLedgerContract.avlEntryUpdate,
        delete: supplierLedgerContract.avlEntryDelete
      }
    },
    settings: supplierLedgerSettingsContract
  },
  laborCoreGovernmentApplication: oc.router({
    documentSelectionPreview: documentSelectionPreviewContract
  }),
  laborCoreOperationPackage: oc.router({
    startPattern: startLaborCoreOperationPackagePatternContract
  }),
  laborCoreEgovApplicationSteps: oc.router({
    listByTag: listEgovApplicationStepsByTagContract,
    listByPackageInstances: listEgovApplicationStepsByPackageInstancesContract
  }),
  laborCoreEgovBulk: oc.router({
    submitByStepInstances: bulkSubmitEgovApplicationsByStepInstancesContract,
    syncByStepInstances: bulkSyncEgovApplicationsByStepInstancesContract
  }),
  laborCoreEgovOperations: oc.router({
    amendByStepInstance: amendEgovApplicationByStepInstanceContract,
    withdrawByStepInstance: withdrawEgovApplicationByStepInstanceContract,
    syncApplication: syncEgovApplicationStatusContract
  }),
  laborCoreEgovSubmit: oc.router({
    submitByStepInstance: submitEgovApplicationByStepInstanceContract
  }),
  laborCoreYearEndAdjustmentInstances: oc.router({
    listByTag: listYearEndAdjustmentInstancesByTagContract,
    documentTabCounts: countYearEndAdjustmentInstancesByTagDocumentTabsContract,
    instanceDocuments: getYearEndAdjustmentInstanceDocumentsContract
  }),
  govProfileSubmittable: govProfileSubmittableContract,
  personAggregateEvents: findPersonHistoriesFromEventStoreContract,
  aiOperatorChat: chatContract,
  oauth2: oauth2Contract,
  openidMetadata: openidMetadataContract,
  ssoIntegrations: ssoIntegrationsContract,
  inboundSso: inboundSsoContract,
  outboundSsoSoftwares: outboundSsoSoftwaresContract,
  boePackageNotificationConfig: boePackageNotificationConfigContract,
  notifResponsePolicies: notificationAndResponsePoliciesContract,
  notifResponsePackageAssignments: notificationAndResponsePackageAssignmentsContract,
  notifResponseRecipientOptions: notificationAndResponseRecipientOptionsContract,
  notifResponseVariables: notificationAndResponseVariablesContract,
  deviceGroup: deviceGroupContract,
  publicId: publicIdContract,
  publicIdDetail: publicIdDetailContract,
  publicIdFiles: publicIdFilesContract,
  publicIdReveal: publicIdRevealContract,
  deviceAssignmentRule: deviceAssignmentRuleContract,
  deviceAssignmentInclusion: deviceAssignmentInclusionContract
});
const link = new OpenAPILink(appContract, {
  url: getBaseUrl() ?? '',
  fetch: authFetch
});
export const orpcClient: ContractRouterClient<typeof appContract> = createORPCClient(link);